import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import { isAfter } from "date-fns";
import { getCurrentLiveTimeRange, isRangeType } from "@/helpers/date.helpers";
import type {
  EnergyBlockTableRow,
  PowerProfileAutomationOption,
  PreselectedRangeEUI,
} from "@/pages/Energy";
import type { DateRange, SelectedDateRange } from "@/types/common";
import type { DeviceData } from "@/types/data";
import type { XAxisRangeType } from "@/types/view";

export type ContractOptions = "Imbalance" | "Wholesale";
export const storageKey: PreselectedRangeEUI = "energy_ui_preselected_range";
// should be supported in all platforms
const createUUID = () => window.crypto.randomUUID();
export const defaultRangeType = "last7days";

export type EnergyBlockState = {
  timeBlocks: DateRange[];
  averagePower: number | undefined;
  contractType: ContractOptions;
  idempotencyKeys: string[];
  transactionId: string;
};

type DateRangeState = {
  currentTime?: number;
  gateClosureTime?: number;
  liveRange: Partial<DateRange>;
  selectedRange: Partial<SelectedDateRange>;
  pollCounter: number;
};

const createInitialState = (): DateRangeState => {
  const storedRange = localStorage.getItem(storageKey) ?? undefined;

  const type = isRangeType(storedRange) ? storedRange : "efaDay";
  let range: Partial<DateRange> = { from: undefined, to: undefined };

  if (type === "live") {
    range = getCurrentLiveTimeRange();
  }

  return {
    currentTime: undefined,
    liveRange: range,
    selectedRange: { ...range, type },
    pollCounter: 0,
  };
};

export type ScheduleEnergyState = {
  deleteEnergyBlockModalToggle?: boolean;
  blockTodelete?: EnergyBlockTableRow;
  profileCreatorModalToggle?: boolean;
  isZoomed: boolean;
  zoomRange?: XAxisRangeType;
  powerProfileAutomationOption: PowerProfileAutomationOption;
  powerProfileTimeBlocks: DateRange[];
  selectedResource?: DeviceData["name"];
  selectedResourceId?: DeviceData["id"];
  nameplatePowerActiveLookup: Record<string, number>;
  isBMUnitLookup: Record<string, boolean>;
  settlementPeriod: number;
} & DateRangeState &
  EnergyBlockState;

const initialState: ScheduleEnergyState = {
  deleteEnergyBlockModalToggle: false,
  blockTodelete: undefined,
  profileCreatorModalToggle: false,
  ...createInitialState(),
  timeBlocks: [],
  averagePower: 0,
  contractType: "Imbalance",
  idempotencyKeys: [],
  transactionId: createUUID(),
  isZoomed: false,
  zoomRange: { min: undefined, max: undefined },
  powerProfileAutomationOption: "disable",
  powerProfileTimeBlocks: [],
  selectedResource: undefined,
  selectedResourceId: undefined,
  nameplatePowerActiveLookup: {},
  isBMUnitLookup: {},
  settlementPeriod: 30,
};

export const energySliceName = "energy";

export const scheduleEnergySlice = createSlice({
  name: energySliceName,
  initialState,
  reducers: {
    openDeleteEnergyBlockModal: (state) => {
      state.deleteEnergyBlockModalToggle = true;
    },
    closeDeleteEnergyBlockModal: (state) => {
      state.deleteEnergyBlockModalToggle = false;
    },
    setBlockTodelete: (
      state,
      action: PayloadAction<EnergyBlockTableRow | undefined>
    ) => {
      state.blockTodelete = action.payload;
    },
    openProfileCreatorAutomationToggleModal: (state) => {
      state.profileCreatorModalToggle = true;
    },
    closeProfileCreatorAutomationToggleModal: (state) => {
      state.profileCreatorModalToggle = false;
    },
    setCurrentTime: (state, action: PayloadAction<number>) => {
      const currentTime = action.payload;
      state.currentTime = currentTime;
      if (state.selectedRange.type === "live") {
        state.liveRange = getCurrentLiveTimeRange(new Date(currentTime));
      }
      // if viewing range is not in the past and not live, increment the pollCounter to poll latest data
      else if (
        state.selectedRange.to &&
        !isAfter(new Date(currentTime), new Date(state.selectedRange.to))
      ) {
        state.pollCounter = state.pollCounter + 1;
      }
    },
    setSelectedRange: (state, action: PayloadAction<SelectedDateRange>) => {
      const selectedRange = action.payload;
      state.selectedRange = selectedRange;
      state.liveRange = {
        from: selectedRange.from,
        to: selectedRange.to,
      };
    },
    setGateClosureTime: (state, action: PayloadAction<number>) => {
      const gateClosure = action.payload;
      state.gateClosureTime = gateClosure;
    },
    setTimeBlocks: (state, action: PayloadAction<DateRange[]>) => {
      const timeBlocks = action.payload;
      const idempotencyKeys = timeBlocks.map(() => createUUID());

      state.timeBlocks = timeBlocks;
      state.idempotencyKeys = idempotencyKeys;
    },
    setAveragePower: (state, action: PayloadAction<number>) => {
      state.averagePower = action.payload;
    },
    setContractType: (state, action: PayloadAction<ContractOptions>) => {
      state.contractType = action.payload;
    },
    resetNewEnergyBlock: (state) => {
      state.averagePower = 0;
      state.contractType = "Imbalance";
      state.idempotencyKeys = [];
      state.transactionId = createUUID();
    },
    toggleZoom: (state, action) => {
      state.isZoomed = action.payload;
    },
    setZoomRange: (state, action) => {
      state.zoomRange = action.payload;
    },
    setPowerProfileAutomationOption: (
      state,
      action: PayloadAction<PowerProfileAutomationOption>
    ) => {
      state.powerProfileAutomationOption = action.payload;
    },
    setPowerProfileAutomationTimeBlocks: (
      state,
      action: PayloadAction<ScheduleEnergyState["powerProfileTimeBlocks"]>
    ) => {
      state.powerProfileTimeBlocks = action.payload;
    },
    setSelectedResource: (state, action: PayloadAction<DeviceData["name"]>) => {
      // TODO: should we have a single setSelected reducer so state is always in sync?
      // e.g.
      // const { name, id = "" } = action.payload;
      // state.selectedResource = name;
      // state.selectedResourceId = id;
      state.selectedResource = action.payload;
    },
    setSelectedResourceId: (state, action: PayloadAction<DeviceData["id"]>) => {
      state.selectedResourceId = action.payload;
    },
    setNameplatePowerActive: (
      state,
      action: PayloadAction<{
        key: string;
        value: number;
      }>
    ) => {
      state.nameplatePowerActiveLookup[action.payload.key] =
        action.payload.value;
    },
    setIsBMUnit: (
      state,
      action: PayloadAction<{
        key: string;
        value: boolean;
      }>
    ) => {
      state.isBMUnitLookup[action.payload.key] = action.payload.value;
    },
  },
});

export const {
  openDeleteEnergyBlockModal,
  closeDeleteEnergyBlockModal,
  setBlockTodelete,
  openProfileCreatorAutomationToggleModal,
  closeProfileCreatorAutomationToggleModal,
  setCurrentTime,
  setSelectedRange,
  setGateClosureTime,
  setTimeBlocks,
  setAveragePower,
  setContractType,
  resetNewEnergyBlock,
  toggleZoom,
  setZoomRange,
  setPowerProfileAutomationOption,
  setPowerProfileAutomationTimeBlocks,
  setSelectedResource,
  setSelectedResourceId,
  setNameplatePowerActive,
  setIsBMUnit,
} = scheduleEnergySlice.actions;

export default scheduleEnergySlice;
